import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ADMIN_ROLE_LABELS as ROLE_LABELS, isSuperAdmin } from '../lib/adminRoles';
import {
  getAdminUsers,
  createAdminUser,
  deleteAdminUser,
  updateAdminUser,
  updateAdminUserPassword,
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Trash2, UserPlus } from 'lucide-react';

const STAFF_ROLES = ['admin', 'tendero', 'cocina'];

const emptyEditForm = {
  name: '',
  email: '',
  username: '',
  role: 'tendero',
  password: '',
  passwordConfirm: '',
};

export default function AdminManageAdmins() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('tendero');

  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState(emptyEditForm);
  const [savingEdit, setSavingEdit] = useState(false);

  const [passwordUser, setPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  const superAdmin = Boolean(user && isSuperAdmin(user.role));

  const load = async () => {
    try {
      const rows = await getAdminUsers();
      setList(Array.isArray(rows) ? rows : []);
    } catch {
      toast.error('No se pudo cargar el personal.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && user && !superAdmin) {
      toast.error('Solo el super administrador puede gestionar personal.');
      navigate('/admin', { replace: true });
    }
  }, [authLoading, user, navigate, superAdmin]);

  useEffect(() => {
    if (user && superAdmin) load();
  }, [user, superAdmin]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await createAdminUser({ name, email, password, role });
      toast.success('Usuario creado. Inicia sesión con su correo y contraseña.');
      setName('');
      setEmail('');
      setPassword('');
      setRole('tendero');
      await load();
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'No se pudo crear el usuario.');
    } finally {
      setCreating(false);
    }
  };

  const openEditDialog = (u) => {
    setEditUser(u);
    setEditForm({
      name: u.name || '',
      email: u.email || '',
      username: u.username || '',
      role: u.role || 'tendero',
      password: '',
      passwordConfirm: '',
    });
  };

  const closeEditDialog = () => {
    setEditUser(null);
    setEditForm(emptyEditForm);
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    if (!editUser) return;
    const payload = {
      name: editForm.name.trim(),
      email: editForm.email.trim(),
      username: editForm.username.trim(),
      role: editForm.role,
    };
    if (editForm.password) {
      if (editForm.password.length < 8) {
        toast.error('La contraseña debe tener al menos 8 caracteres.');
        return;
      }
      if (editForm.password !== editForm.passwordConfirm) {
        toast.error('Las contraseñas no coinciden.');
        return;
      }
      payload.password = editForm.password;
    }
    setSavingEdit(true);
    try {
      await updateAdminUser(editUser.id, payload);
      toast.success('Usuario actualizado');
      closeEditDialog();
      await load();
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'No se pudo guardar los cambios.');
    } finally {
      setSavingEdit(false);
    }
  };

  const openPasswordDialog = (u) => {
    setPasswordUser(u);
    setNewPassword('');
    setNewPasswordConfirm('');
  };

  const closePasswordDialog = () => {
    setPasswordUser(null);
    setNewPassword('');
    setNewPasswordConfirm('');
  };

  const handlePasswordSave = async (e) => {
    e.preventDefault();
    if (!passwordUser) return;
    if (newPassword.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (newPassword !== newPasswordConfirm) {
      toast.error('Las contraseñas no coinciden.');
      return;
    }
    setSavingPassword(true);
    try {
      await updateAdminUserPassword(passwordUser.id, newPassword);
      toast.success(`Contraseña actualizada para ${passwordUser.email}`);
      closePasswordDialog();
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'No se pudo cambiar la contraseña.');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleDelete = async (id, userRole) => {
    if (!STAFF_ROLES.includes(userRole)) {
      toast.error('No se puede eliminar desde aquí.');
      return;
    }
    if (!window.confirm('¿Eliminar este usuario? No podrá volver a entrar al panel.')) return;
    try {
      await deleteAdminUser(id);
      toast.success('Usuario eliminado.');
      await load();
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'No se pudo eliminar.');
    }
  };

  if (authLoading || (user && loading)) {
    return <div className="text-muted-foreground animate-pulse">Cargando personal…</div>;
  }

  if (!superAdmin) {
    return null;
  }

  return (
    <div className="max-w-3xl space-y-8" data-testid="admin-manage-admins">
      <div>
        <h1 className="font-heading text-2xl font-bold text-foreground">Credenciales de personal</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Crea y edita usuarios cajero, cocina o administrador. Pueden iniciar sesión con correo o
          nombre de usuario.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <UserPlus className="w-5 h-5" />
            Nuevo usuario
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreate} className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="adm_name">Nombre</Label>
              <Input id="adm_name" value={name} onChange={(e) => setName(e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="adm_email">Correo</Label>
              <Input
                id="adm_email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="adm_pass">Contraseña</Label>
              <Input
                id="adm_pass"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
              />
            </div>
            <div className="space-y-2">
              <Label>Rol</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tendero">Cajero (tendero)</SelectItem>
                  <SelectItem value="cocina">Cocina</SelectItem>
                  <SelectItem value="admin">Administrador</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Button type="submit" disabled={creating}>
                {creating ? 'Creando…' : 'Crear usuario'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Usuarios del panel</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="divide-y divide-border">
            {list.map((u) => (
              <li key={u.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-foreground">{u.name || u.email}</p>
                  <p className="text-sm text-muted-foreground">
                    {u.email} · {ROLE_LABELS[u.role] || u.role}
                  </p>
                  {u.username ? (
                    <p className="text-xs text-muted-foreground">Usuario: {u.username}</p>
                  ) : null}
                </div>
                {STAFF_ROLES.includes(u.role) ? (
                  <div className="flex flex-wrap items-center gap-2 shrink-0">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => openEditDialog(u)}
                    >
                      Editar
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => openPasswordDialog(u)}
                    >
                      Contraseña
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDelete(u.id, u.role)}
                    >
                      <Trash2 className="w-4 h-4 sm:mr-1" />
                      <span className="hidden sm:inline">Eliminar</span>
                    </Button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <Dialog open={!!editUser} onOpenChange={(open) => !open && closeEditDialog()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar usuario</DialogTitle>
          </DialogHeader>
          {editUser ? (
            <form onSubmit={handleEditSave} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit_name">Nombre</Label>
                <Input
                  id="edit_name"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_email">Correo</Label>
                <Input
                  id="edit_email"
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_username">Usuario (login)</Label>
                <Input
                  id="edit_username"
                  value={editForm.username}
                  onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                  required
                  minLength={2}
                  placeholder="ej. cocina1"
                />
                <p className="text-xs text-muted-foreground">
                  También puede iniciar sesión con este nombre, sin el correo.
                </p>
              </div>
              <div className="space-y-2">
                <Label>Rol</Label>
                <Select
                  value={editForm.role}
                  onValueChange={(v) => setEditForm({ ...editForm, role: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tendero">Cajero (tendero)</SelectItem>
                    <SelectItem value="cocina">Cocina</SelectItem>
                    <SelectItem value="admin">Administrador</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="border-t pt-4 space-y-2">
                <p className="text-sm font-medium text-foreground">Nueva contraseña (opcional)</p>
                <Input
                  type="password"
                  value={editForm.password}
                  onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                  placeholder="Dejar vacío para no cambiar"
                  autoComplete="new-password"
                />
                <Input
                  type="password"
                  value={editForm.passwordConfirm}
                  onChange={(e) => setEditForm({ ...editForm, passwordConfirm: e.target.value })}
                  placeholder="Confirmar contraseña"
                  autoComplete="new-password"
                />
              </div>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button type="button" variant="outline" onClick={closeEditDialog}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={savingEdit}>
                  {savingEdit ? 'Guardando…' : 'Guardar cambios'}
                </Button>
              </DialogFooter>
            </form>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={!!passwordUser} onOpenChange={(open) => !open && closePasswordDialog()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Cambiar contraseña</DialogTitle>
          </DialogHeader>
          {passwordUser ? (
            <form onSubmit={handlePasswordSave} className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Usuario:{' '}
                <span className="font-medium text-foreground">
                  {passwordUser.name || passwordUser.email}
                </span>
              </p>
              <div className="space-y-2">
                <Label htmlFor="new_pass">Nueva contraseña</Label>
                <Input
                  id="new_pass"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new_pass_confirm">Confirmar contraseña</Label>
                <Input
                  id="new_pass_confirm"
                  type="password"
                  value={newPasswordConfirm}
                  onChange={(e) => setNewPasswordConfirm(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={closePasswordDialog}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={savingPassword}>
                  {savingPassword ? 'Guardando…' : 'Guardar contraseña'}
                </Button>
              </DialogFooter>
            </form>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
